import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { defaultLocale, type Locale } from "@/lib/i18n/config";
import { dictionaries, type Dictionary } from "@/lib/i18n/dictionaries";
import { ensureLocaleTranslations } from "@/lib/data";

type DictSection = keyof Dictionary;
type DictKey = { [S in DictSection]: `${S}.${Extract<keyof Dictionary[S], string>}` }[DictSection];

interface LocaleContextValue {
  locale: Locale;
  t: (key: DictKey) => string;
}

const LocaleContext = createContext<LocaleContextValue>({
  locale: defaultLocale,
  t: (key) => key,
});

export function LocaleProvider({ locale, children }: { locale: Locale; children: ReactNode }) {
  // On first load, main.tsx has already awaited this for `locale` before hydrating (see its
  // comment), so this is a no-op there. It matters for the in-app language switcher
  // (`LanguageModal`), which navigates client-side with no reload: switching to a locale whose
  // chunk hasn't loaded yet needs to fetch it and then re-render item/location names with it,
  // without redoing the whole page. `translationsReady` exists purely to change `value`'s
  // identity once that fetch resolves — nothing reads its value directly.
  const [translationsReady, setTranslationsReady] = useState(0);
  useEffect(() => {
    let cancelled = false;
    ensureLocaleTranslations(locale).then(() => {
      if (!cancelled) setTranslationsReady((n) => n + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [locale]);

  const value = useMemo<LocaleContextValue>(() => {
    const dict = dictionaries[locale] ?? dictionaries[defaultLocale];
    const t = (key: DictKey): string => {
      const [section, field] = key.split(".") as [DictSection, string];
      const sectionValue = dict[section] as Record<string, string> | undefined;
      const value = sectionValue?.[field];
      if (typeof value === "string") return value;
      const fallbackSection = dictionaries[defaultLocale][section] as Record<string, string> | undefined;
      return fallbackSection?.[field] ?? key;
    };
    return { locale, t };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- translationsReady is a re-render
    // trigger only; the value it produces never changes based on it.
  }, [locale, translationsReady]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  return useContext(LocaleContext);
}
